import { useRef, useMemo, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Text, Billboard, Stars } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import * as THREE from "three";

const SUN_SPEED = 4.5;
const TRAIL_DURATION = 14;
const TRAIL_POINTS = 450;

const PLANETS = [
  { name: "Mercury", radius: 1.1,  speed: 4.8,  size: 0.055, color: "#b5b5b5", trail: [0.5, 0.6, 1.0] },
  { name: "Venus",   radius: 1.65, speed: 3.0,  size: 0.095, color: "#e8cda0", trail: [0.4, 0.5, 1.0] },
  { name: "Earth",   radius: 2.3,  speed: 2.1,  size: 0.10,  color: "#2255cc", trail: [0.3, 0.5, 1.0] },
  { name: "Mars",    radius: 3.1,  speed: 1.4,  size: 0.065, color: "#c1440e", trail: [0.4, 0.5, 1.0] },
  { name: "Jupiter", radius: 4.7,  speed: 0.65, size: 0.30,  color: "#c88b3a", trail: [0.35, 0.45, 1.0] },
  { name: "Saturn",  radius: 6.1,  speed: 0.42, size: 0.24,  color: "#d4b483", trail: [0.35, 0.45, 1.0], hasRings: true },
  { name: "Uranus",  radius: 7.4,  speed: 0.26, size: 0.16,  color: "#7de8e8", trail: [0.3, 0.4, 1.0] },
  { name: "Neptune", radius: 8.5,  speed: 0.16, size: 0.15,  color: "#3f54ba", trail: [0.3, 0.4, 1.0] },
];

function smoothstep(t) {
  return t * t * (3 - 2 * t);
}

function makeGeo() {
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(TRAIL_POINTS * 3), 3));
  g.setAttribute("color",    new THREE.BufferAttribute(new Float32Array(TRAIL_POINTS * 3), 3));
  return g;
}

function ComputedTrail({ getPos, rgb, geometry }) {
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const pos = geometry.attributes.position.array;
    const col = geometry.attributes.color.array;
    for (let i = 0; i < TRAIL_POINTS; i++) {
      const pastT = t - (i / (TRAIL_POINTS - 1)) * TRAIL_DURATION;
      const p = getPos(pastT);
      pos[i * 3]     = p[0];
      pos[i * 3 + 1] = p[1];
      pos[i * 3 + 2] = p[2];
      const brightness = Math.pow(1 - i / TRAIL_POINTS, 1.8) * 3.0;
      col[i * 3]     = rgb[0] * brightness;
      col[i * 3 + 1] = rgb[1] * brightness;
      col[i * 3 + 2] = rgb[2] * brightness;
    }
    geometry.attributes.position.needsUpdate = true;
    geometry.attributes.color.needsUpdate = true;
  });

  return (
    <line frustumCulled={false} geometry={geometry}>
      <lineBasicMaterial vertexColors toneMapped={false} />
    </line>
  );
}

function SolarSystem({ viewMode, isMobile }) {
  const sunRef = useRef();
  const starsRef = useRef();
  const planetGroupRefs = useRef(PLANETS.map(() => null));
  const sunTrailGeo     = useMemo(() => makeGeo(), []);
  const planetTrailGeos = useMemo(() => PLANETS.map(() => makeGeo()), []);

  const transition = useRef({ progress: 1, fromMode: "side", toMode: "side" });
  const prevMode   = useRef("side");

  useFrame(({ clock, camera }, delta) => {
    const t = clock.getElapsedTime();
    const sunX = t * SUN_SPEED;

    if (viewMode !== prevMode.current) {
      transition.current = { progress: 0, fromMode: prevMode.current, toMode: viewMode };
      prevMode.current = viewMode;
    }

    const tr = transition.current;
    tr.progress = Math.min(1, tr.progress + delta / 1.6);
    const eased = smoothstep(tr.progress);

    const sideP  = isMobile ? new THREE.Vector3(sunX,      3, 22) : new THREE.Vector3(sunX - 4,  4, 30);
    const sideLK = isMobile ? new THREE.Vector3(sunX,      0,  0) : new THREE.Vector3(sunX - 4,  0,  0);
    const frontP  = isMobile ? new THREE.Vector3(sunX + 25, 0,  0) : new THREE.Vector3(sunX + 40, 0, 0);
    const frontLK = new THREE.Vector3(sunX,  0,  0);

    const fromP  = tr.fromMode === "side" ? sideP  : frontP;
    const toP    = tr.toMode   === "side" ? sideP  : frontP;
    const fromLK = tr.fromMode === "side" ? sideLK : frontLK;
    const toLK   = tr.toMode   === "side" ? sideLK : frontLK;

    const arc = Math.sin(tr.progress * Math.PI) * 8;

    camera.position.lerpVectors(fromP, toP, eased);
    camera.position.y += arc;
    camera.lookAt(new THREE.Vector3().lerpVectors(fromLK, toLK, eased));

    if (sunRef.current) sunRef.current.position.set(sunX, 0, 0);
    if (starsRef.current) starsRef.current.position.set(sunX, 0, 0);
    PLANETS.forEach((p, idx) => {
      const grp = planetGroupRefs.current[idx];
      if (!grp) return;
      grp.position.set(sunX, Math.cos(t * p.speed) * p.radius, Math.sin(t * p.speed) * p.radius);
    });
  });

  return (
    <>
      <group ref={starsRef}>
        <Stars radius={200} depth={30} count={60000} factor={3} saturation={0} fade speed={0.3} />
      </group>
      <group ref={sunRef}>
        <pointLight intensity={3} color="#fff5aa" distance={50} />
        <mesh>
          <sphereGeometry args={[0.7, 32, 32]} />
          <meshStandardMaterial color="#fff5aa" emissive="#ffcc33" emissiveIntensity={3.0} toneMapped={false} />
        </mesh>
        <Billboard position={[0, 1.1, 0]}>
          <Text fontSize={0.16} color="#ffee88" anchorX="center" anchorY="bottom" font="/fonts/spartan.ttf">
            Sun
          </Text>
        </Billboard>
      </group>

      <ComputedTrail getPos={(t) => [t * SUN_SPEED, 0, 0]} rgb={[1.0, 0.85, 0.2]} geometry={sunTrailGeo} />

      {PLANETS.map((p, idx) => (
        <group key={p.name}>
          <group ref={(el) => { planetGroupRefs.current[idx] = el; }}>
            <mesh>
              <sphereGeometry args={[p.size, 20, 20]} />
              <meshStandardMaterial color={p.color} emissive={p.color} emissiveIntensity={0.7} toneMapped={false} />
            </mesh>
            {p.hasRings && (
              <mesh rotation={[Math.PI / 2.2, 0.2, 0]}>
                <ringGeometry args={[p.size * 1.5, p.size * 2.4, 64]} />
                <meshStandardMaterial color="#c8a87a" side={THREE.DoubleSide} transparent opacity={0.65} />
              </mesh>
            )}
            <Billboard position={[0, p.size + 0.15, 0]}>
              <Text fontSize={0.2} color="#cccccc" anchorX="center" anchorY="bottom" font="/fonts/spartan.ttf">
                {p.name}
              </Text>
            </Billboard>
          </group>
          <ComputedTrail
            getPos={(t) => [
              t * SUN_SPEED,
              Math.cos(t * p.speed) * p.radius,
              Math.sin(t * p.speed) * p.radius,
            ]}
            rgb={p.trail}
            geometry={planetTrailGeos[idx]}
          />
        </group>
      ))}

      <ambientLight intensity={0.04} />
      <EffectComposer>
        <Bloom mipmapBlur intensity={0.5} luminanceThreshold={0.3} luminanceSmoothing={0.1} />
      </EffectComposer>
    </>
  );
}

function SunScene() {
  const [viewMode, setViewMode] = useState("side");
  const isSide = viewMode === "side";
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  return (
    <div style={{ position: "relative", height: "100vh" }}>
      <Canvas
        camera={{ position: isMobile ? [0, 3, 22] : [-4, 4, 30], fov: isMobile ? 42 : 26, far: 2000 }}
        style={{ height: "100%", background: "#000005" }}
      >
        <SolarSystem viewMode={viewMode} isMobile={isMobile} />
      </Canvas>

      <a
        href="https://www.forbes.com/sites/startswithabang/2018/08/30/our-motion-through-space-isnt-a-vortex-but-something-far-more-interesting/"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: "absolute",
          bottom: "28px",
          left: "28px",
          color: "rgba(255,255,255,0.4)",
          fontSize: "11px",
          fontFamily: "milker",
          letterSpacing: "0.06em",
          textDecoration: "none",
          transition: "color 0.2s",
        }}
        onMouseEnter={e => { e.currentTarget.style.color = "rgba(255,255,255,0.8)"; }}
        onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.4)"; }}
      >
        INSPIRED BY FORBES
      </a>

      <button
        onClick={() => setViewMode(isSide ? "front" : "side")}
        style={{
          position: "absolute",
          bottom: "28px",
          right: "28px",
          padding: "10px 22px",
          background: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.18)",
          borderRadius: "8px",
          color: "rgba(255,255,255,0.85)",
          fontSize: "12px",
          fontFamily: "milker",
          letterSpacing: "0.1em",
          cursor: "pointer",
          backdropFilter: "blur(10px)",
          transition: "background 0.2s, border-color 0.2s",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = "rgba(255,255,255,0.14)";
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = "rgba(255,255,255,0.07)";
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
        }}
      >
        {isSide ? "FRONT VIEW" : "SIDE VIEW"}
      </button>
    </div>
  );
}

export default SunScene;
